import { type Immutable } from 'immer';
import { GRID_SIZE, MAX_WIDGETS_PER_LAYOUT } from '../constants';
import { computeDefaultPositions } from './gridPositions';

/**
 * When we add a new widget, we'll try to fix a widget of these dimensions into
 * the grid, starting with the first one, and proceeding to each subsequent one
 * if there is no room.
 */
export const DEFAULT_WIDGET_SIZES = [
  { w: GRID_SIZE, h: 2 },
  { w: 3, h: 2 },
  { w: 1, h: 1 },
];

/** Check to see if two items overlap. */
export function overlaps(
  a: { x: number; y: number; w: number; h: number },
  b: { x: number; y: number; w: number; h: number },
): boolean {
  const aMinX = a.x;
  const aMinY = a.y;
  const aMaxX = a.x + a.w;
  const aMaxY = a.y + a.h;

  const bMinX = b.x;
  const bMinY = b.y;
  const bMaxX = b.x + b.w;
  const bMaxY = b.y + b.h;

  /** check if boundaries of a and b intersect on the x and y axis */
  return aMinX < bMaxX && aMaxX > bMinX && aMinY < bMaxY && aMaxY > bMinY;
}

/** Find a "hole" in our layout where we can fit a widget. Use in future iterations for adding customizable widgets */
export function findHoleInLayout(
  widgets: Immutable<{ position: { x: number; y: number; w: number; h: number } }[]>,
  itemWidth: number,
  itemHeight: number,
): { x: number; y: number; w: number; h: number } | undefined {
  for (let x = 0; x <= GRID_SIZE - itemWidth; x++) {
    for (let y = 0; y <= GRID_SIZE - itemHeight; y++) {
      const candidate = { x, y, w: itemWidth, h: itemHeight };
      if (!widgets.some((item) => overlaps(item.position, candidate))) {
        return candidate;
      }
    }
  }

  return undefined;
}

/** Find a location to put a new widget into a layout. */
export function nextWidgetPosition(
  widgets: Immutable<{ position: { x: number; y: number; w: number; h: number } }[]> | undefined,
): { x: number; y: number; w: number; h: number } | undefined {
  const currentWidgetCount = widgets?.length || 0;
  if (currentWidgetCount >= MAX_WIDGETS_PER_LAYOUT) return undefined;

  const positions = computeDefaultPositions(currentWidgetCount + 1);
  return positions[positions.length - 1];
}

/** Returns true if any part of the card is outside the grid. */
export function cardPositionIsInvalid(position: {
  x: number;
  y: number;
  w: number;
  h: number;
}): boolean {
  return position.y + position.h > GRID_SIZE || position.x + position.w > GRID_SIZE;
}

export const safeDivide = (numerator: number | null, denominator: number | null): number => {
  if (!denominator || denominator === 0) {
    console.warn('SDM_ERROR: Division by zero! ');
    return 0;
  }
  return (numerator ?? 0) / denominator;
};
